import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { timetableService, Board, Entry, Lesson, Run, RunOptions } from '../../../services/timetableService';
import { buildIndex } from './boardModel';

const POLL_MS = 1500;

/**
 * One timetable of the session on the board: its data, edits applied at once
 * and confirmed by the server (rolled back with its reason when refused),
 * and a running generation polled until it finishes.
 */
export function useBoard(sessionId: string | undefined, status: 'draft' | 'published') {
  const { enqueueSnackbar } = useSnackbar();
  const [board, setBoard] = useState<Board | null>(null);
  const [loading, setLoading] = useState(true);
  const [run, setRun] = useState<Run | null>(null);
  const request = useRef(0);

  const load = useCallback(async (quiet = false) => {
    if (!sessionId) return;
    const id = ++request.current;
    if (!quiet) setLoading(true);
    try {
      const data = await timetableService.getBoard(sessionId, status);
      if (id !== request.current) return;
      setBoard(data);
      setRun(data.run && (data.run.status === 'running' || data.run.status === 'review') ? data.run : null);
    } catch (err: any) {
      if (id === request.current) enqueueSnackbar(err?.message || 'Could not load the timetable', { variant: 'error' });
    } finally {
      if (id === request.current) setLoading(false);
    }
  }, [sessionId, status, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const index = useMemo(() => (board ? buildIndex(board) : null), [board]);
  const timetableId = board?.timetable?._id;

  /** Apply a change now, confirm it with the server, and undo it if refused. */
  const optimistic = useCallback(async (
    apply: (entries: Entry[]) => Entry[],
    send: () => Promise<Entry | Entry[] | void>,
    settle?: (entries: Entry[], result: Entry | Entry[] | void) => Entry[],
  ) => {
    let before: Entry[] = [];
    setBoard((b) => {
      if (!b) return b;
      before = b.entries;
      return { ...b, entries: apply(b.entries) };
    });
    try {
      const result = await send();
      if (settle) setBoard((b) => (b ? { ...b, entries: settle(b.entries, result) } : b));
      return true;
    } catch (err: any) {
      setBoard((b) => (b ? { ...b, entries: before } : b));
      enqueueSnackbar(err?.message || 'That change was not saved', { variant: 'warning' });
      return false;
    }
  }, [enqueueSnackbar]);

  const replaceEntries = (entries: Entry[], updated: Entry[]) => {
    const byId = new Map(updated.map((e) => [e._id, e]));
    return entries.map((e) => byId.get(e._id) ?? e);
  };

  const move = useCallback((entry: Entry, day: number, period: number) => optimistic(
    (entries) => entries.map((e) => (e._id === entry._id ? { ...e, day, period } : e)),
    () => timetableService.move(timetableId!, entry._id, day, period),
    (entries, result) => replaceEntries(entries, [result as Entry]),
  ), [optimistic, timetableId]);

  const swap = useCallback((a: Entry, b: Entry) => optimistic(
    (entries) => entries.map((e) => (e._id === a._id ? { ...e, day: b.day, period: b.period } : e._id === b._id ? { ...e, day: a.day, period: a.period } : e)),
    () => timetableService.swap(timetableId!, a._id, b._id),
    (entries, result) => replaceEntries(entries, result as Entry[]),
  ), [optimistic, timetableId]);

  const place = useCallback((lesson: Lesson, day: number, period: number, span: 1 | 2) => {
    const tempId = `new-${Date.now()}`;
    return optimistic(
      (entries) => [...entries, {
        _id: tempId, day, period, span, classId: lesson.classId, sectionInstanceId: lesson.sectionInstanceId,
        subjectId: lesson.subjectId, teacherId: lesson.teacherId, roomId: null, locked: false,
      }],
      () => timetableService.place(timetableId!, lesson.key, day, period, span),
      (entries, result) => entries.map((e) => (e._id === tempId ? (result as Entry) : e)),
    );
  }, [optimistic, timetableId]);

  const unplace = useCallback((entry: Entry) => optimistic(
    (entries) => entries.filter((e) => e._id !== entry._id),
    () => timetableService.unplace(timetableId!, entry._id),
  ), [optimistic, timetableId]);

  const setLocked = useCallback((entries: Entry[], locked: boolean) => {
    const ids = new Set(entries.map((e) => e._id));
    return optimistic(
      (all) => all.map((e) => (ids.has(e._id) ? { ...e, locked } : e)),
      () => timetableService.lock(timetableId!, Array.from(ids), locked),
    );
  }, [optimistic, timetableId]);

  /** A generation ended: say how it went and show the result, or hold it for review. */
  const finish = useCallback((done: Run) => {
    if (done.status === 'review') { setRun(done); return; }
    setRun(null);
    if (done.status === 'discarded') {
      enqueueSnackbar('Nothing was changed', { variant: 'info' });
    } else if (done.status === 'done') {
      const r = done.result!;
      enqueueSnackbar(
        r.unplaced ? `Timetable generated. ${r.unplaced} period${r.unplaced === 1 ? '' : 's'} could not be placed.` : 'Timetable generated. Every period is placed.',
        { variant: r.unplaced ? 'warning' : 'success' },
      );
    } else {
      enqueueSnackbar(done.error || 'Generation failed', { variant: 'error' });
    }
    load(true);
  }, [enqueueSnackbar, load]);

  // A running generation: poll it until it ends.
  useEffect(() => {
    if (!run || run.status !== 'running') return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const next = await timetableService.getRun(run._id);
        if (cancelled) return;
        if (next.status === 'running') setRun(next);
        else finish(next);
      } catch (err: any) {
        if (!cancelled) enqueueSnackbar(err?.message || 'Lost track of the generation', { variant: 'error' });
      }
    }, POLL_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [run, finish, enqueueSnackbar]);

  const startRun = useCallback(async (options: RunOptions) => {
    try {
      setRun(await timetableService.startRun(timetableId!, options));
      return true;
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not start generating', { variant: 'error' });
      return false;
    }
  }, [timetableId, enqueueSnackbar]);

  const stopRun = useCallback(async () => {
    if (!run) return;
    try {
      const next = await timetableService.stopRun(run._id);
      if (next.status === 'running') setRun(next);
      else finish(next);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not stop', { variant: 'error' });
    }
  }, [run, finish, enqueueSnackbar]);

  /** Accept or turn down a result held for review. */
  const settleReview = useCallback(async (accept: boolean) => {
    if (!run) return;
    try {
      finish(await (accept ? timetableService.applyRun(run._id) : timetableService.discardRun(run._id)));
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'That didn\u2019t go through', { variant: 'error' });
    }
  }, [run, finish, enqueueSnackbar]);

  return { board, index, loading, reload: load, run, startRun, stopRun, settleReview, move, swap, place, unplace, setLocked };
}

export type BoardApi = ReturnType<typeof useBoard>;
